import { useEffect, useRef, useState } from 'react';
import { PiBellDuotone, PiChecksDuotone, PiXBold } from 'react-icons/pi';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, cn, useTheme } from '@v2/design-system';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';

type NotificationAction = {
    name?: string;
    label?: string | null;
    url?: string | null;
    shouldOpenUrlInNewTab?: boolean;
    shouldMarkAsRead?: boolean;
};

type Notification = {
    id: string;
    title: string;
    body: string | null;
    icon: string | null;
    icon_color: string | null;
    actions?: NotificationAction[];
    read_at: string | null;
    created_at: string | null;
    kind?: string;
};

type NotificationsResponse = {
    data: Notification[];
    unread_count: number;
};

function timeAgo(iso: string | null): string {
    if (!iso) return '';
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
}

function primaryAction(n: Notification): NotificationAction | null {
    const action = (n.actions ?? []).find((a) => a.url);
    return action ?? null;
}

function isStockApproval(n: Notification): boolean {
    return n.kind === 'stock_approval' || n.id.startsWith('stock-approval-');
}

export default function HeaderNotifications() {
    const { t } = useI18n();
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);
    const queryClient = useQueryClient();

    const { data } = useQuery({
        queryKey: ['v2', 'notifications'],
        queryFn: async () => (await api.get<NotificationsResponse>('/notifications')).data,
        refetchInterval: 60_000,
        staleTime: 30_000,
    });

    const invalidate = () => queryClient.invalidateQueries({ queryKey: ['v2', 'notifications'] });

    const markAll = useMutation({
        mutationFn: () => api.post('/notifications/mark-all-read'),
        onSuccess: invalidate,
    });

    const markOne = useMutation({
        mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
        onSuccess: invalidate,
    });

    const dismissOne = useMutation({
        mutationFn: (id: string) => api.post(`/notifications/${id}/dismiss`),
        onSuccess: invalidate,
    });

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const unread = data?.unread_count ?? 0;
    const notifications = data?.data ?? [];
    const canClearAll = notifications.length > 0;

    const openNotification = (n: Notification) => {
        const action = primaryAction(n);

        if (action?.url) {
            if (action.shouldOpenUrlInNewTab) {
                window.open(action.url, '_blank', 'noopener,noreferrer');
            } else {
                navigate(action.url);
            }
            setOpen(false);
        }

        if (!isStockApproval(n) && !n.read_at && (action?.shouldMarkAsRead !== false || !action)) {
            markOne.mutate(n.id);
        }
    };

    const clearNotification = (n: Notification, e: React.MouseEvent) => {
        e.stopPropagation();
        dismissOne.mutate(n.id);
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label={t('v2.header.notifications')}
                className={cn(
                    'relative flex h-9 w-9 items-center justify-center rounded-full border transition-colors',
                    isDark
                        ? 'border-gray-700 bg-gray-800 hover:bg-gray-700'
                        : 'border-gray-200 bg-white hover:bg-gray-50',
                )}
            >
                <PiBellDuotone className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                {unread > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-4 min-w-4 animate-pulse items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                        {unread > 99 ? '99+' : unread}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className={cn(
                        'absolute right-0 top-full z-50 mt-2 w-80 rounded-xl border shadow-xl sm:w-96',
                        isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white',
                    )}
                >
                    <div
                        className={cn(
                            'flex items-center justify-between border-b px-4 py-3',
                            isDark ? 'border-gray-700' : 'border-gray-100',
                        )}
                    >
                        <span className={cn('text-sm font-semibold', isDark ? 'text-gray-100' : 'text-gray-800')}>
                            {t('v2.header.notifications')}
                            {unread > 0 && (
                                <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-600 dark:bg-red-900/40 dark:text-red-400">
                                    {unread}
                                </span>
                            )}
                        </span>
                        {canClearAll && (
                            <button
                                type="button"
                                onClick={() => markAll.mutate()}
                                disabled={markAll.isPending}
                                className={cn(
                                    'flex items-center gap-1 rounded text-xs transition-colors disabled:opacity-50',
                                    isDark
                                        ? 'text-gray-400 hover:text-gray-200'
                                        : 'text-gray-500 hover:text-gray-700',
                                )}
                                title={t('v2.header.mark_all_read')}
                            >
                                <PiChecksDuotone className="h-4 w-4" />
                                {t('v2.header.mark_all_read')}
                            </button>
                        )}
                    </div>

                    <ul className="max-h-80 divide-y overflow-y-auto">
                        {notifications.length === 0 ? (
                            <li className={cn('px-4 py-8 text-center text-sm', isDark ? 'text-gray-500' : 'text-gray-400')}>
                                {t('v2.header.no_notifications')}
                            </li>
                        ) : (
                            notifications.map((n) => {
                                const action = primaryAction(n);

                                return (
                                    <li key={n.id}>
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => openNotification(n)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    openNotification(n);
                                                }
                                            }}
                                            className={cn(
                                                'flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left transition-colors',
                                                n.read_at
                                                    ? isDark
                                                        ? 'hover:bg-gray-800'
                                                        : 'hover:bg-gray-50'
                                                    : isDark
                                                      ? 'bg-amber-950/25 hover:bg-amber-950/40'
                                                      : 'bg-amber-50/70 hover:bg-amber-50',
                                            )}
                                        >
                                            <span
                                                className={cn(
                                                    'mt-1 h-2 w-2 shrink-0 rounded-full',
                                                    n.read_at ? 'bg-transparent' : 'bg-amber-500',
                                                )}
                                            />
                                            <div className="min-w-0 flex-1 overflow-hidden">
                                                <div className="flex items-start justify-between gap-2">
                                                    <p
                                                        className={cn(
                                                            'truncate text-sm font-medium',
                                                            isDark ? 'text-gray-100' : 'text-gray-800',
                                                        )}
                                                    >
                                                        {n.title}
                                                    </p>
                                                    <div className="flex shrink-0 items-center gap-1">
                                                        {n.created_at && (
                                                            <span
                                                                className={cn(
                                                                    'text-[10px]',
                                                                    isDark ? 'text-gray-600' : 'text-gray-400',
                                                                )}
                                                            >
                                                                {timeAgo(n.created_at)}
                                                            </span>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={(e) => clearNotification(n, e)}
                                                            disabled={dismissOne.isPending}
                                                            title={t('v2.header.dismiss_notification')}
                                                            aria-label={t('v2.header.dismiss_notification')}
                                                            className={cn(
                                                                'rounded p-0.5 transition-colors disabled:opacity-50',
                                                                isDark
                                                                    ? 'text-gray-500 hover:bg-gray-800 hover:text-gray-200'
                                                                    : 'text-gray-400 hover:bg-gray-200 hover:text-gray-700',
                                                            )}
                                                        >
                                                            <PiXBold className="size-3.5" />
                                                        </button>
                                                    </div>
                                                </div>
                                                {n.body && (
                                                    <p
                                                        className={cn(
                                                            'mt-0.5 line-clamp-3 text-xs',
                                                            isDark ? 'text-gray-400' : 'text-gray-500',
                                                        )}
                                                    >
                                                        {n.body}
                                                    </p>
                                                )}
                                                {action?.label && action.url && (
                                                    <div className="mt-2">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-7 px-2 text-xs"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openNotification(n);
                                                            }}
                                                        >
                                                            {action.label}
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
